import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
} from '@roger/ui-components';
import { Building2, Loader2, Plus, Search } from 'lucide-react';
import {
  listAgenciesWithAssignedRoles,
  createAgency,
  listUserRoleAssignments,
  removeUserFromAgency,
  getAgencyMembers,
  listRoles,
  inviteUser,
  updateUserRoles,
} from '../../services/api';
import type {
  AgencyResponse,
  AgencyWithAssignedRolesResponse,
  UserManagerUserResponse,
} from '../../services/api/types.gen';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import type { AgencyState, UserRolesState } from './types';
import AgencyRow from './components/AgencyRow';
import EditUserRolesDialog from './components/EditUserRolesDialog';
import InviteUserToAgencyDialog, {
  type InviteUserProfileDraft,
} from '../../pages/RBAC/components/InviteUserToAgencyDialog';
import ConfirmRemoveDialog from './components/ConfirmRemoveDialog';
import CreateAgencyDialog from './components/CreateAgencyDialog';
import { filterUserVisibleRoles } from '../../utils/rbacRoles';
import { getUserFacingApiMessage } from '../../utils/apiErrorDetail';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

const SuperUserTab: React.FC = () => {
  const { showError, showSuccess } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
    }))
  );

  const [agencies, setAgencies] = useState<AgencyWithAssignedRolesResponse[]>(
    []
  );
  const [totalAgencies, setTotalAgencies] = useState(0);
  const [page, setPage] = useState(0);
  const [isLoadingAgencies, setIsLoadingAgencies] = useState(true);

  const [agencyStates, setAgencyStates] = useState<Record<string, AgencyState>>(
    {}
  );

  const [editingUser, setEditingUser] = useState<{
    agency: AgencyResponse;
    user: UserManagerUserResponse;
  } | null>(null);
  const [userRolesState, setUserRolesState] = useState<UserRolesState>({
    roles: null,
    availableRoles: null,
    isLoading: false,
  });

  const [addUserTarget, setAddUserTarget] = useState<AgencyResponse | null>(
    null
  );

  const [removeTarget, setRemoveTarget] = useState<{
    agency: AgencyResponse;
    user: UserManagerUserResponse;
  } | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  const [showCreateAgency, setShowCreateAgency] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Track the latest in-flight load so out-of-order responses can't overwrite
  // newer state with older data (search + pagination changes can race).
  const loadTokenRef = useRef(0);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchQuery]);

  // ---------------------------------------------------------------------------
  // Load a page of agencies (with assigned roles inline, server-side search).
  // Replaces the previous "load all + N×listDistinctRolesAssignedAtAgency"
  // pattern that caused a request storm on each page load. We also call this
  // after every mutation, so role badges stay fresh without a separate
  // per-agency refresh endpoint.
  // ---------------------------------------------------------------------------

  const loadAgencies = useCallback(async () => {
    const token = ++loadTokenRef.current;
    setIsLoadingAgencies(true);
    try {
      const res = await listAgenciesWithAssignedRoles({
        query: {
          search: debouncedSearch || undefined,
          skip: page * PAGE_SIZE,
          limit: PAGE_SIZE,
        },
      });

      if (token !== loadTokenRef.current) return;

      const items = res.data?.items ?? [];
      const total = res.data?.total ?? 0;
      setAgencies(items);
      setTotalAgencies(total);
      setAgencyStates(prev =>
        Object.fromEntries(
          items.map(a => {
            const previous = prev[a.id];
            return [
              a.id,
              {
                users: previous?.users ?? null,
                isLoadingUsers: previous?.isLoadingUsers ?? false,
                isExpanded: previous?.isExpanded ?? false,
                assignedRoles: filterUserVisibleRoles(a.assigned_roles ?? []),
              } satisfies AgencyState,
            ];
          })
        )
      );
    } catch (error) {
      if (token !== loadTokenRef.current) return;
      console.error('Failed to load agencies:', error);
      showError(
        'Failed to Load Agencies',
        getUserFacingApiMessage(
          error,
          'Unable to load agencies. Please try again.'
        )
      );
    } finally {
      if (token === loadTokenRef.current) {
        setIsLoadingAgencies(false);
      }
    }
  }, [debouncedSearch, page, showError]);

  useEffect(() => {
    loadAgencies();
  }, [loadAgencies]);

  // -------------------------------------------------------------------------
  // Expand / collapse agency (lazy load users)
  // -------------------------------------------------------------------------

  const handleToggleExpand = useCallback(
    async (agencyId: string) => {
      const current = agencyStates[agencyId];
      if (!current) return;

      const willExpand = !current.isExpanded;
      setAgencyStates(prev => ({
        ...prev,
        [agencyId]: { ...prev[agencyId], isExpanded: willExpand },
      }));

      if (willExpand && current.users === null) {
        setAgencyStates(prev => ({
          ...prev,
          [agencyId]: { ...prev[agencyId], isLoadingUsers: true },
        }));
        try {
          const res = await getAgencyMembers({
            query: { agency_id: agencyId },
          });
          setAgencyStates(prev => ({
            ...prev,
            [agencyId]: {
              ...prev[agencyId],
              users: res.data ?? [],
              isLoadingUsers: false,
            },
          }));
        } catch (error) {
          console.error('Failed to load agency users:', error);
          showError(
            'Failed to Load Users',
            getUserFacingApiMessage(
              error,
              'Unable to load users. Please try again.'
            )
          );
          setAgencyStates(prev => ({
            ...prev,
            [agencyId]: { ...prev[agencyId], isLoadingUsers: false },
          }));
        }
      }
    },
    [agencyStates, showError]
  );

  // -------------------------------------------------------------------------
  // Edit user roles
  // -------------------------------------------------------------------------

  const handleEditUser = useCallback(
    async (agency: AgencyResponse, user: UserManagerUserResponse) => {
      setEditingUser({ agency, user });
      setUserRolesState({ roles: null, availableRoles: null, isLoading: true });

      try {
        const [rolesRes, availableRes] = await Promise.all([
          listUserRoleAssignments({
            path: { user_id: user.id },
            query: { agency_id: agency.id },
          }),
          listRoles({
            query: { agency_id: agency.id },
          }),
        ]);
        const allAvailable = availableRes.data ?? [];
        const agencyScopedAll = allAvailable
          .filter(r => r.scope !== 'clinic')
          .sort(
            (a, b) =>
              Number(a.is_internal ?? false) - Number(b.is_internal ?? false)
          );
        const agencyScopedAvailable = filterUserVisibleRoles(agencyScopedAll);
        const agencyScopedRoleIds = new Set(agencyScopedAll.map(r => r.id));
        const userRoles = (rolesRes.data ?? []).filter(r =>
          agencyScopedRoleIds.has(r.role_id)
        );
        setUserRolesState({
          roles: userRoles,
          availableRoles: agencyScopedAvailable,
          isLoading: false,
        });
      } catch (error) {
        console.error('Failed to load user roles:', error);
        showError(
          'Failed to Load Roles',
          getUserFacingApiMessage(
            error,
            'Unable to load role data. Please try again.'
          )
        );
        setUserRolesState(prev => ({ ...prev, isLoading: false }));
      }
    },
    [showError]
  );

  const handleSaveRoles = useCallback(
    async (roleIds: string[]) => {
      if (!editingUser) return;
      const agencyId = editingUser.agency.id;
      try {
        // Without throwOnError the client returns errors instead of throwing,
        // so this path used to toast success even when the API rejected the
        // save (common when "select all" includes restricted roles) — EMR-1630.
        await updateUserRoles({
          path: { user_id: editingUser.user.id },
          query: { agency_id: agencyId },
          body: { role_ids: roleIds },
          throwOnError: true,
        });
        showSuccess('Roles updated successfully.');
        setEditingUser(null);

        // Reload agencies first (updates role badges, preserves isExpanded).
        await loadAgencies();

        // Then refresh the users list for this agency so name/info changes are
        // immediately visible without requiring the user to collapse and re-expand.
        try {
          const res = await getAgencyMembers({
            query: { agency_id: agencyId },
          });
          setAgencyStates(prev => ({
            ...prev,
            [agencyId]: { ...prev[agencyId], users: res.data ?? [] },
          }));
        } catch (refreshError) {
          console.error('Failed to refresh agency members:', refreshError);
        }
      } catch (error) {
        console.error('Failed to update user roles:', error);
        showError(
          'Failed to Update Roles',
          getUserFacingApiMessage(
            error,
            'Unable to save roles. Please try again.'
          )
        );
      }
    },
    [editingUser, loadAgencies, showError, showSuccess]
  );

  // -------------------------------------------------------------------------
  // Remove user from agency
  // -------------------------------------------------------------------------

  const handleConfirmRemove = useCallback(async () => {
    if (!removeTarget) return;
    setIsRemoving(true);
    try {
      await removeUserFromAgency({
        path: {
          agency_id: removeTarget.agency.id,
          user_id: removeTarget.user.id,
        },
      });
      showSuccess('User removed from agency.');
      setRemoveTarget(null);

      setAgencyStates(prev => ({
        ...prev,
        [removeTarget.agency.id]: {
          ...prev[removeTarget.agency.id],
          users: (prev[removeTarget.agency.id]?.users ?? []).filter(
            u => u.id !== removeTarget.user.id
          ),
        },
      }));
      void loadAgencies();
    } catch (error) {
      console.error('Failed to remove user from agency:', error);
      showError(
        'Failed to Remove User',
        getUserFacingApiMessage(
          error,
          'Unable to remove user. Please try again.'
        )
      );
    } finally {
      setIsRemoving(false);
    }
  }, [loadAgencies, removeTarget, showError, showSuccess]);

  // -------------------------------------------------------------------------
  // Add user to agency
  // -------------------------------------------------------------------------

  const handleAddUser = useCallback(
    async (params: {
      emails: string[];
      agency_id: string;
      role_ids: string[];
      profiles: InviteUserProfileDraft[];
    }) => {
      if (!addUserTarget) return;

      try {
        // Without throwOnError a 403 (e.g. Super Admin on a non-internal
        // invitee) resolves instead of throwing, res.data is empty, and we
        // fall through to "Invitation sent." — same class of bug as EMR-1630.
        const res = await inviteUser({
          body: {
            emails: params.emails,
            role_ids: params.role_ids,
            agency_id: params.agency_id,
            profiles: params.profiles,
          },
          throwOnError: true,
        });
        const data = res.data;
        if (data && data.failed_count > 0 && data.invited_count > 0) {
          showSuccess(
            `${data.invited_count} of ${params.emails.length} invitations sent. ${data.failed_count} failed.`
          );
        } else if (data && data.failed_count === params.emails.length) {
          showError(
            'Failed to Add Users',
            'Unable to send invitations. Please try again.'
          );
          return;
        } else {
          showSuccess('Invitation sent.');
        }
        setAddUserTarget(null);
        void loadAgencies();

        const agencyState = agencyStates[params.agency_id];
        if (agencyState?.isExpanded) {
          setAgencyStates(prev => ({
            ...prev,
            [params.agency_id]: {
              ...prev[params.agency_id],
              users: null,
              isLoadingUsers: true,
            },
          }));
          try {
            const res = await getAgencyMembers({
              query: { agency_id: params.agency_id },
            });
            setAgencyStates(prev => ({
              ...prev,
              [params.agency_id]: {
                ...prev[params.agency_id],
                users: res.data ?? [],
                isLoadingUsers: false,
              },
            }));
          } catch (refreshError) {
            console.error('Failed to refresh agency members:', refreshError);
            showError(
              'Failed to Refresh Members',
              getUserFacingApiMessage(
                refreshError,
                'Unable to reload member list. Please try again.'
              )
            );
            setAgencyStates(prev => ({
              ...prev,
              [params.agency_id]: {
                ...prev[params.agency_id],
                isLoadingUsers: false,
              },
            }));
          }
        }
      } catch (error) {
        console.error('Failed to add user to agency:', error);
        showError(
          'Failed to Add User',
          getUserFacingApiMessage(
            error,
            'Unable to invite user. Please try again.'
          )
        );
      }
    },
    [addUserTarget, agencyStates, loadAgencies, showError, showSuccess]
  );

  // -------------------------------------------------------------------------
  // Create agency
  // -------------------------------------------------------------------------

  const handleCreateAgency = useCallback(
    async (data: {
      system_name: string;
      written_name: string;
      abbreviation: string;
      is_rogeremr_enabled: boolean;
      is_kinnser_enabled: boolean;
      add_onboarding_team: boolean;
      sync_clinic_info: boolean;
    }) => {
      try {
        const { data: createdAgency, error } = await createAgency({
          body: {
            ...data,
            skip_workos_organization: false,
          },
        });
        if (error) {
          throw error;
        }

        const warnings =
          (createdAgency as { warnings?: string[] } | undefined)?.warnings ??
          [];
        if (warnings.length > 0) {
          showError('Agency Created with Warnings', warnings.join(' '));
        } else {
          showSuccess('Agency created successfully.');
        }
        setShowCreateAgency(false);
        await loadAgencies();
      } catch (error) {
        console.error('Failed to create agency:', error);
        showError(
          'Failed to Create Agency',
          getUserFacingApiMessage(
            error,
            'Unable to create agency. Please try again.'
          )
        );
      }
    },
    [loadAgencies, showError, showSuccess]
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const totalPages = Math.max(1, Math.ceil(totalAgencies / PAGE_SIZE));
  const showingFrom = totalAgencies === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min((page + 1) * PAGE_SIZE, totalAgencies);

  return (
    <div className='space-y-4'>
      <Card>
        <CardHeader className='flex flex-row items-center justify-between gap-4 py-4 px-5'>
          <div>
            <CardTitle className='text-base'>All Agencies</CardTitle>
            <p className='text-sm text-muted-foreground'>
              Manage users and roles across every agency in the system.
            </p>
          </div>
          <Button
            size='sm'
            onClick={() => setShowCreateAgency(true)}
            className='gap-1.5 flex-shrink-0'
          >
            <Plus className='h-4 w-4' />
            New agency
          </Button>
        </CardHeader>

        <CardContent className='p-0 border-t border-border/50'>
          <div className='px-5 py-3 border-b border-border/50 bg-muted/20'>
            <label htmlFor='superuser-agency-search' className='sr-only'>
              Search agencies by name, system name, or abbreviation
            </label>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none' />
              <Input
                id='superuser-agency-search'
                type='search'
                placeholder='Search by agency name, system name, or abbreviation…'
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                inputSize='default'
                className='pl-10'
                autoComplete='off'
              />
            </div>
          </div>

          {isLoadingAgencies && agencies.length === 0 ? (
            <div className='flex items-center justify-center py-16 gap-2 text-muted-foreground'>
              <Loader2 className='h-5 w-5 animate-spin' />
              <span>Loading agencies…</span>
            </div>
          ) : agencies.length === 0 ? (
            debouncedSearch ? (
              <div className='flex flex-col items-center justify-center py-12 gap-1 text-muted-foreground'>
                <Search className='h-5 w-5' />
                <p className='text-sm'>
                  No agencies match &quot;{debouncedSearch}&quot;
                </p>
              </div>
            ) : (
              <div className='flex flex-col items-center justify-center py-16 gap-2 text-muted-foreground'>
                <Building2 className='h-8 w-8' />
                <p>No agencies found.</p>
              </div>
            )
          ) : (
            <div className='divide-y divide-border/50'>
              {agencies.map(agency => {
                const state = agencyStates[agency.id];
                if (!state) return null;
                return (
                  <AgencyRow
                    key={agency.id}
                    agency={agency}
                    state={state}
                    onToggleExpand={handleToggleExpand}
                    onEditUser={handleEditUser}
                    onRemoveUser={(a, u) =>
                      setRemoveTarget({ agency: a, user: u })
                    }
                    onAddUser={a => setAddUserTarget(a)}
                  />
                );
              })}
            </div>
          )}

          {totalAgencies > 0 && (
            <div className='flex items-center justify-between gap-3 px-5 py-3 border-t border-border/50 bg-muted/10'>
              <span className='text-xs text-muted-foreground'>
                {isLoadingAgencies ? (
                  <span className='inline-flex items-center gap-1'>
                    <Loader2 className='h-3 w-3 animate-spin' />
                    Loading…
                  </span>
                ) : (
                  <>
                    Showing {showingFrom}–{showingTo} of {totalAgencies}
                  </>
                )}
              </span>
              <div className='flex gap-2'>
                <Button
                  size='sm'
                  variant='outline'
                  disabled={page <= 0 || isLoadingAgencies}
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  size='sm'
                  variant='outline'
                  disabled={page >= totalPages - 1 || isLoadingAgencies}
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <EditUserRolesDialog
        open={editingUser !== null}
        agency={editingUser?.agency ?? null}
        user={editingUser?.user ?? null}
        rolesState={userRolesState}
        onClose={() => setEditingUser(null)}
        onSave={handleSaveRoles}
      />

      <InviteUserToAgencyDialog
        open={addUserTarget !== null}
        agencies={agencies}
        agencyId={addUserTarget?.id ?? null}
        onClose={() => setAddUserTarget(null)}
        onInvite={handleAddUser}
      />

      <ConfirmRemoveDialog
        open={removeTarget !== null}
        agency={removeTarget?.agency ?? null}
        user={removeTarget?.user ?? null}
        isRemoving={isRemoving}
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleConfirmRemove}
      />

      <CreateAgencyDialog
        open={showCreateAgency}
        onClose={() => setShowCreateAgency(false)}
        existingAgencies={agencies}
        onCreate={handleCreateAgency}
      />
    </div>
  );
};

export default SuperUserTab;
